// ===========================
// dashboard-store.js
// ===========================

const dashboardStore = () => ({
  // Estado Principal
  isInitialized: false,
  isOpen: false,
  isMobile: window.innerWidth < 1024,
  // Sidebars
  leftSidebarOpen: true,
  rightSidebarOpen: true,
  // Menu e Navegação
  menu: {},
  currentPath: window.location.pathname,
  // Carousel
  currentSlide: 0,
  slides: [],
  carouselInterval: null,
  // Créditos
  credits: {
    wcoinc: 0,
    wcoinp: 0,
    goblinpoints: 0,
    credits: 0,
    ruud: 0
  },
  // Configurações
  transitionDuration: 300,
  carouselAutoPlayInterval: 10000,
  // Inicialização
  init(menuData = {}) {
    this.menu = menuData;
    this.loadState();
    this.setupEventListeners();
    this.updateMobileState();
    this.isInitialized = true;
  },
  // Gerenciamento de Estado
  loadState() {
    try {
      const saved = sessionStorage.getItem('dashboardState');
      if (saved) {
        const state = JSON.parse(saved);
        this.leftSidebarOpen = state.leftSidebarOpen ?? true;
        this.rightSidebarOpen = state.rightSidebarOpen ?? true;
        this.currentSlide = state.currentSlide ?? 0;
      }
    } catch (e) {
      console.error('Error loading dashboard state:', e);
    }
  },
  saveState() {
    try {
      const state = {
        leftSidebarOpen: this.leftSidebarOpen,
        rightSidebarOpen: this.rightSidebarOpen,
        currentSlide: this.currentSlide,
        timestamp: Date.now()
      };
      sessionStorage.setItem('dashboardState', JSON.stringify(state));
    } catch (e) {
      console.error('Error saving dashboard state:', e);
    }
  },
  // Event Listeners
  setupEventListeners() {
    // Responsive
    window.addEventListener('resize', this.debounce(() => {
      this.updateMobileState();
      this.handleResize();
    }, 250));

    // HTMX Events
    document.body.addEventListener('htmx:afterSwap', () => {
      this.updateCurrentPath();
    });
  },
  // Utilidades
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  },
  // Dashboard Controls
  open() {
    this.isOpen = true;
    document.body.classList.add('overflow-hidden');
  },
  close() {
    this.isOpen = false;
    document.body.classList.remove('overflow-hidden');
  },
  toggle() {
    this.isOpen ? this.close() : this.open();
  },
  // Sidebar Controls
  toggleLeftSidebar() {
    this.leftSidebarOpen = !this.leftSidebarOpen;
    this.saveState();
  },
  toggleRightSidebar() {
    this.rightSidebarOpen = !this.rightSidebarOpen;
    this.saveState();
  },
  // Responsive Handlers
  updateMobileState() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 1024;

    // Auto-collapse on mobile
    if (this.isMobile && !wasMobile) {
      this.leftSidebarOpen = false;
      this.rightSidebarOpen = false;
    } else if (!this.isMobile && wasMobile) {
      this.leftSidebarOpen = true;
      this.rightSidebarOpen = true;
    }
  },
  handleResize() {
    // Trigger Alpine reactivity with safe fallback
    if (this.$nextTick && typeof this.$nextTick === 'function') {
      this.$nextTick(() => {
        this.saveState();
      });
    } else if (typeof Alpine !== 'undefined' && Alpine.nextTick) {
      Alpine.nextTick(() => {
        this.saveState();
      });
    } else {
      // Fallback to requestAnimationFrame or setTimeout
      const fallback = window.requestAnimationFrame || (cb => setTimeout(cb, 0));
      fallback(() => {
        this.saveState();
      });
    }
  },
  // Menu Helpers
  updateCurrentPath() {
    this.currentPath = window.location.pathname;
  },
  isMenuItemActive(url) {
    if (!url) return false;
    // Normalize both URLs for comparison
    const normalizedUrl = url.startsWith('/') ? url : '/' + url;
    const normalizedPath = this.currentPath.startsWith('/') ? this.currentPath : '/' + this.currentPath;
    return normalizedPath === normalizedUrl;
  },
  // Carousel Controls
  nextSlide() {
    if (this.slides.length > 0) {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      this.saveState();
    }
  },
  prevSlide() {
    if (this.slides.length > 0) {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.saveState();
    }
  },
  goToSlide(index) {
    if (index >= 0 && index < this.slides.length) {
      this.currentSlide = index;
      this.saveState();
    }
  },
  startCarousel() {
    this.stopCarousel();
    if (this.slides.length > 1) {
      this.carouselInterval = setInterval(() => {
        this.nextSlide();
      }, this.carouselAutoPlayInterval);
    }
  },
  stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  },
  // Credits Management
  updateCredits(type, value) {
    if (this.credits.hasOwnProperty(type)) {
      this.credits[type] = value;
    }
  },
  updateAllCredits(creditsData) {
    Object.assign(this.credits, creditsData);
  },
  // Computed Properties (getters)
  get sidebarClasses() {
    return {
      left: this.leftSidebarOpen ? 'w-120' : 'w-20',
      right: this.rightSidebarOpen ? 'w-120' : 'w-20'
    };
  },
  get dashboardClasses() {
    return {
      'opacity-0': !this.isOpen,
      'opacity-100': this.isOpen,
      'pointer-events-none': !this.isOpen
    };
  },
  // Validation
  validateMenu(menu) {
    if (!menu || typeof menu !== 'object') {
      console.warn('Invalid menu data provided');
      return false;
    }
    return true;
  },
  // Development helpers
  get isDevelopment() {
    return window.developmentMode || false;
  }
});


// ===========================
// components.js
// ===========================

const sidebarComponent = () => ({
  // Props (passed from parent)
  side: 'left',
  // 'left' or 'right'

  init() {
    // Component initialization if needed
  },
  get isOpen() {
    return this.side === 'left' ? this.$store.dashboard.leftSidebarOpen : this.$store.dashboard.rightSidebarOpen;
  },
  get sidebarClasses() {
    const baseClasses = 'transition-all duration-300 ease-in-out';
    const widthClass = this.isOpen ? 'w-80' : 'w-20';
    return `${baseClasses} ${widthClass}`;
  },
  get iconClasses() {
    if (this.side === 'left') {
      return this.isOpen ? 'fa-chevron-left' : 'fa-chevron-right';
    }
    return this.isOpen ? 'fa-chevron-right' : 'fa-chevron-left';
  },
  toggle() {
    if (this.side === 'left') {
      this.$store.dashboard.toggleLeftSidebar();
    } else {
      this.$store.dashboard.toggleRightSidebar();
    }
  }
});

const menuComponent = () => ({
  menuGroups: [],
  init() {
    this.generateMenu();
    // Watch for menu changes
    this.$watch('$store.dashboard.menu', () => {
      this.generateMenu();
    });
  },
  generateMenu() {
    const menu = this.$store.dashboard.menu;
    if (!menu) return;
    this.menuGroups = Object.entries(menu).map(([key, group]) => ({
      id: key,
      ...group,
      items: group.items ? group.items.filter(item => {
        return !item.development || this.$store.dashboard.isDevelopment;
      }) : []
    }));
  },
  isActive(url) {
    if (!url) return false;
    // Normalize the URL to match against current path
    const normalizedUrl = url.startsWith('/') ? url : '/' + url;
    return this.$store.dashboard.isMenuItemActive(normalizedUrl);
  },
  handleMenuClick(item) {
    // Update current path immediately for reactive UI
    this.$store.dashboard.updateCurrentPath();
    return true;
  },
  get showLabels() {
    return this.$store.dashboard.leftSidebarOpen;
  }
});

const carouselComponent = () => ({
  slides: [],
  autoPlay: true,
  init() {
    this.initSlides();
    if (this.autoPlay) {
      this.$store.dashboard.startCarousel();
    }

    // Pause on hover
    this.$el.addEventListener('mouseenter', () => {
      this.$store.dashboard.stopCarousel();
    });
    this.$el.addEventListener('mouseleave', () => {
      if (this.autoPlay) {
        this.$store.dashboard.startCarousel();
      }
    });
  },
  initSlides() {
    const slideElements = this.$el.querySelectorAll('[data-carousel-item]');
    this.slides = Array.from(slideElements).map((el, index) => ({
      index,
      element: el,
      active: index === this.$store.dashboard.currentSlide
    }));
    this.$store.dashboard.slides = this.slides;
  },
  get currentSlide() {
    return this.$store.dashboard.currentSlide;
  },
  next() {
    this.$store.dashboard.nextSlide();
  },
  prev() {
    this.$store.dashboard.prevSlide();
  },
  goTo(index) {
    this.$store.dashboard.goToSlide(index);
  },
  isActive(index) {
    return this.currentSlide === index;
  }
});

const creditsComponent = () => ({
  animatingCredits: {},
  init() {
    // Load initial credits via HTMX if needed
    this.loadCredits();

    // Watch for credit changes and animate
    this.$watch('$store.dashboard.credits', (newCredits, oldCredits) => {
      if (oldCredits) {
        Object.keys(newCredits).forEach(type => {
          if (newCredits[type] !== oldCredits[type]) {
            this.animateCredit(type);
          }
        });
      }
    });
  },
  loadCredits() {
    // Trigger HTMX request if element has hx-get
    const creditsElement = document.getElementById('userCredits');
    if (creditsElement && creditsElement.hasAttribute('hx-get')) {
      htmx.trigger(creditsElement, 'load');
    }
  },
  animateCredit(type) {
    this.animatingCredits[type] = true;
    setTimeout(() => {
      this.animatingCredits[type] = false;
    }, 500);
  },
  getCreditClasses(type) {
    return {
      'credit-update-animation': this.animatingCredits[type],
      'transition-all duration-300': true
    };
  },
  formatNumber(value) {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value || '0';
  }
});

const languageSelectorComponent = () => ({
  isOpen: false,
  currentLanguage: 'en',
  init() {
    // Get current language from cookie or meta
    this.currentLanguage = this.getCurrentLanguage();

    // Close dropdown when clicking outside
    document.addEventListener('click', e => {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    });
  },
  getCurrentLanguage() {
    const cookie = document.cookie.split('; ').find(row => row.startsWith('language='));
    return cookie ? cookie.split('=')[1] : 'en';
  },
  toggle() {
    this.isOpen = !this.isOpen;
  },
  selectLanguage(lang) {
    this.currentLanguage = lang;
    this.isOpen = false;

    // Set cookie and reload page
    document.cookie = `language=${lang}; path=/; max-age=${365 * 24 * 60 * 60}`;
    window.location.reload();
  }
});

const helpdeskComponent = () => ({
  init() {
    // Component initialization
  },
  openHelpdesk() {
    // Handle helpdesk modal or navigation
    console.log('Opening helpdesk...');
  }
});

const notificationComponent = () => ({
  notifications: [],
  init() {
    // Listen for global notification events
    window.addEventListener('show-notification', e => {
      this.showNotification(e.detail);
    });
  },
  showNotification(notification) {
    const id = Date.now();
    const notif = {
      id,
      type: notification.type || 'info',
      title: notification.title || '',
      message: notification.message || '',
      duration: notification.duration || 5000
    };
    this.notifications.push(notif);

    // Auto remove
    if (notif.duration > 0) {
      setTimeout(() => {
        this.removeNotification(id);
      }, notif.duration);
    }
  },
  removeNotification(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
  }
});

const serverStatusComponent = () => ({
  status: {
    online: false,
    players: 0,
    maxPlayers: 1000
  },
  init() {
    this.loadServerStatus();

    // Refresh every 30 seconds
    setInterval(() => {
      this.loadServerStatus();
    }, 30000);
  },
  loadServerStatus() {
    // Mock data - replace with actual endpoint when available
    this.status = {
      online: true,
      players: Math.floor(Math.random() * 800) + 100,
      maxPlayers: 1000
    };
  },
  get statusClasses() {
    return this.status.online ? 'bg-green-500' : 'bg-red-500';
  },
  get statusText() {
    return this.status.online ? 'Online' : 'Offline';
  }
});


// ===========================
// theme-selector.js
// ===========================

const themeSelectorComponent = () => ({
  isOpen: false,
  currentTheme: 'darkOrange',
  isDebugMode: false,
  themes: [
    {
      id: 'darkOrange',
      name: 'Dark Orange',
      icon: 'fa-fire',
      color: '#fbbf24'
    },
    {
      id: 'darkRed',
      name: 'Dark Red',
      icon: 'fa-skull',
      color: '#ef4444'
    },
    {
      id: 'darkGreen',
      name: 'Dark Green',
      icon: 'fa-leaf',
      color: '#10b981'
    },
    {
      id: 'cyberpunkNeon',
      name: 'Cyberpunk',
      icon: 'fa-bolt',
      color: '#ff00ff'
    },
    {
      id: 'dark',
      name: 'Dark',
      icon: 'fa-moon',
      color: '#6b7280'
    },
    {
      id: 'light',
      name: 'Light',
      icon: 'fa-sun',
      color: '#f59e0b'
    },
    {
      id: 'darkPurple',
      name: 'Dark Purple',
      icon: 'fa-dragon',
      color: '#a855f7'
    }
  ],
  init() {
    // Get current theme from HTML data-theme attribute
    const htmlTheme = document.documentElement.getAttribute('data-theme');
    if (htmlTheme) {
      this.currentTheme = htmlTheme;
    }

    // Load saved theme preference
    this.loadThemePreference();

    // Apply initial theme
    this.applyTheme(this.currentTheme);

    // Listen for theme change events
    window.addEventListener('theme-changed', e => {
      this.currentTheme = e.detail.theme;
      this.applyTheme(this.currentTheme);
    });
  },
  loadThemePreference() {
    // Try to load from localStorage
    const savedTheme = localStorage.getItem('dashboard-theme');
    if (savedTheme && this.themes.find(t => t.id === savedTheme)) {
      this.currentTheme = savedTheme;
    }

    // Server-specific themes
    const server = document.querySelector('meta[name="game-server"]')?.content;
    if (server && !savedTheme) {
      // Auto-select theme based on server
      const serverThemes = {
        'chaos': 'darkRed',
        'bless': 'darkGreen',
        'soul': 'cyberpunkNeon',
        'default': 'darkOrange'
      };
      this.currentTheme = serverThemes[server] || serverThemes['default'];
    }
  },
  selectTheme(themeId) {
    this.currentTheme = themeId;
    this.applyTheme(themeId);
    this.saveThemePreference(themeId);
    this.isOpen = false;

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('theme-changed', {
      detail: {
        theme: themeId
      }
    }));

    // Show notification
    if (window.Alpine && Alpine.store('dashboard')) {
      const theme = this.themes.find(t => t.id === themeId);
      window.dispatchEvent(new CustomEvent('show-notification', {
        detail: {
          type: 'success',
          title: 'Theme Changed',
          message: `Theme changed to ${theme.name}`,
          duration: 3000
        }
      }));
    }
  },
  applyTheme(themeId) {
    // Update HTML data-theme attribute
    document.documentElement.setAttribute('data-theme', themeId);

    // Update body classes for Tailwind
    document.body.className = document.body.className.replace(/theme-\w+/g, '').trim() + ` theme-${themeId}`;

    // Apply server-specific overrides
    const server = document.querySelector('meta[name="game-server"]')?.content;
    if (server) {
      document.documentElement.setAttribute('data-server', server);
    }
  },
  saveThemePreference(themeId) {
    localStorage.setItem('dashboard-theme', themeId);

    // Optionally save to server via AJAX
    if (window.htmx) {
      fetch('/ajax/save-theme-preference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          theme: themeId
        })
      }).catch(error => {
        console.error('Failed to save theme preference:', error);
      });
    }
  },
  getCurrentTheme() {
    return this.themes.find(t => t.id === this.currentTheme);
  },
  toggle() {
    this.isOpen = !this.isOpen;
  },
  previewTheme(themeId) {
    // Temporarily apply theme
    document.documentElement.setAttribute('data-theme-preview', themeId);
  },
  clearPreview() {
    document.documentElement.removeAttribute('data-theme-preview');
  }
});


// ===========================
// Main initialization
// ===========================

document.addEventListener('alpine:init', () => {
  // Register global store
  Alpine.store('dashboard', dashboardStore());

  // Register components
  Alpine.data('sidebar', sidebarComponent);
  Alpine.data('menu', menuComponent);
  Alpine.data('carousel', carouselComponent);
  Alpine.data('credits', creditsComponent);
  Alpine.data('languageSelector', languageSelectorComponent);
  Alpine.data('helpdesk', helpdeskComponent);
  Alpine.data('notifications', notificationComponent);
  Alpine.data('serverStatus', serverStatusComponent);
  Alpine.data('themeSelector', themeSelectorComponent);

  // Global utility functions
  Alpine.magic('formatNumber', () => value => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value || '0';
  });
  
  Alpine.magic('formatCurrency', () => (value, currency = '') => {
    const formatted = typeof value === 'number' ? value.toLocaleString() : value || '0';
    return currency ? `${formatted} ${currency}` : formatted;
  });
});

// HTMX Integration
document.addEventListener('DOMContentLoaded', () => {
  // HTMX Configuration for Dashboard
  document.body.addEventListener('htmx:configRequest', event => {
    event.detail.headers['X-Requested-With'] = 'XMLHttpRequest';
    event.detail.headers['X-HTMX-Request'] = 'true';
  });

  // Handle HTMX loading states
  document.body.addEventListener('htmx:beforeRequest', event => {
    // Show loading indicator
    const loadingIndicator = document.getElementById('loading-indicator');
    if (loadingIndicator && loadingIndicator._x_dataStack) {
      loadingIndicator._x_dataStack[0].show = true;
    }
  });

  document.body.addEventListener('htmx:afterRequest', event => {
    // Hide loading indicator
    const loadingIndicator = document.getElementById('loading-indicator');
    if (loadingIndicator && loadingIndicator._x_dataStack) {
      loadingIndicator._x_dataStack[0].show = false;
    }
  });

  // Handle HTMX content swaps with Alpine
  document.body.addEventListener('htmx:afterSwap', event => {
    // Ensure Alpine processes new content
    if (window.Alpine) {
      Alpine.initTree(event.detail.target);
    }

    // Update current path in store
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').updateCurrentPath();
    }
  });

  // Handle HTMX errors
  document.body.addEventListener('htmx:responseError', event => {
    console.error('HTMX Error:', event.detail);

    // Show notification if component is available
    const notificationEvent = new CustomEvent('show-notification', {
      detail: {
        type: 'error',
        title: 'Request Failed',
        message: 'Failed to load content. Please try again.',
        duration: 5000
      }
    });
    window.dispatchEvent(notificationEvent);
  });

  // Handle successful HTMX requests
  document.body.addEventListener('htmx:afterRequest', event => {
    if (event.detail.successful) {
      // Update credits if the response contains credit data
      const response = event.detail.xhr.response;
      try {
        const data = JSON.parse(response);
        if (data.credits && Alpine.store('dashboard')) {
          Alpine.store('dashboard').updateAllCredits(data.credits);
        }
      } catch (e) {}
    }
  });
});

// Global Dashboard Functions
window.dashboard = {
  init: menu => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').init(menu);
    }
  },
  toggle: () => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').toggle();
    }
  },
  open: () => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').open();
    }
  },
  close: () => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').close();
    }
  },
  toggleLeftSidebar: () => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').toggleLeftSidebar();
    }
  },
  toggleRightSidebar: () => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').toggleRightSidebar();
    }
  },
  updateCredits: (type, value) => {
    if (Alpine.store('dashboard')) {
      Alpine.store('dashboard').updateCredits(type, value);
    }
  }
};

// Export functions for module usage
const initializeDashboard = menu => {
  if (Alpine.store('dashboard')) {
    Alpine.store('dashboard').init(menu);
  }
};

const toggleDashboard = () => window.dashboard.toggle();
const toggleLeftSidebar = () => window.dashboard.toggleLeftSidebar();
const toggleRightSidebar = () => window.dashboard.toggleRightSidebar();
const closeDashboard = () => window.dashboard.close();
const updateCredits = (type, value) => window.dashboard.updateCredits(type, value);